import React, { useState, useRef, useCallback, useEffect } from 'react'
import Webcam from 'react-webcam'
import Avatar from './components/Avatar'
import PhotoRealisticAvatar from './components/PhotoRealisticAvatar'
import MeetingRoomReservation from './components/MeetingRoomReservation'
import AdminDashboard from './components/AdminDashboard'
import LoginScreen from './components/LoginScreen'
import AdminPanel from './components/AdminPanel'
import ReceptionLayout from './components/ReceptionLayout'
import AdminReceptionLayout from './components/AdminReceptionLayout'
import { alsokTheme, componentThemes, uiTokens, themeHelpers } from './styles/alsok-theme'
import { avatarService } from './services/avatarService'
import { DEFAULT_RECEPTION_STATUS, loadReceptionStatus, saveReceptionStatus } from './utils/receptionStatusDefaults'
import { receptionStatusManager } from './services/receptionStatusManager'
import { useReceptionStatus } from './hooks/useReceptionStatus'

// Mode and Permission System
const MODES = {
  LOGIN: 'login',
  ADMIN_DASHBOARD: 'admin-dashboard', // 管理ダッシュボード（旧admin-panel）
  ADMIN_RECEPTION: 'admin-reception', // 管理者用受付モード
  CUSTOMER_RECEPTION: 'customer-reception', // お客様用受付モード
  ROOM_BOOKING: 'room-booking', // 会議室予約
}

const ROLE_PERMISSIONS = {
  administrator: [
    MODES.ADMIN_DASHBOARD,
    MODES.ADMIN_RECEPTION, 
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ],
  staff: [
    MODES.ADMIN_DASHBOARD,
    MODES.ADMIN_RECEPTION,
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ],
  'reception-only': [
    MODES.CUSTOMER_RECEPTION,
    MODES.ROOM_BOOKING
  ]
}

// Mode helper functions
const canAccessMode = (userRole, mode) => {
  return ROLE_PERMISSIONS[userRole]?.includes(mode) || false
}

const getDefaultModeForRole = (userRole) => {
  if (userRole === 'reception-only') {
    return MODES.CUSTOMER_RECEPTION
  }
  return MODES.ADMIN_DASHBOARD
}

// Secure mode switching function
const switchToMode = (setCurrentMode, currentUser, targetMode) => {
  if (!currentUser) {
    console.warn('User not logged in, redirecting to login')
    setCurrentMode(MODES.LOGIN)
    return false
  }
  
  if (!canAccessMode(currentUser.role, targetMode)) {
    console.warn(`Access denied: ${currentUser.role} cannot access ${targetMode}`)
    return false
  }
  
  setCurrentMode(targetMode)
  return true
}

const App = () => {
  const webcamRef = useRef(null)
  const [currentUser, setCurrentUser] = useState(null) // ログイン状態
  const [currentMode, setCurrentMode] = useState(MODES.LOGIN) // 現在のモード
  const [speechText, setSpeechText] = useState('')
  const [faceResult, setFaceResult] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [error, setError] = useState('')
  const [avatarMessage, setAvatarMessage] = useState('')
  
  // グローバル受付状態の管理（フックを使用）
  const receptionStatusHook = useReceptionStatus()
  const receptionStatus = {
    status: receptionStatusHook.status.status,
    startTime: new Date(receptionStatusHook.status.updatedAt),
    totalOperationTime: DEFAULT_RECEPTION_STATUS.totalOperationTime,
    todayOperationTime: DEFAULT_RECEPTION_STATUS.todayOperationTime,
    maintenanceMessage: receptionStatusHook.status.maintenanceMessage || ''
  }
  const [avatarStatus, setAvatarStatus] = useState('ready')
  const [faceAuthEnabled, setFaceAuthEnabled] = useState(true)
  const [currentVisitor, setCurrentVisitor] = useState(null)
  const [currentAudio, setCurrentAudio] = useState(null)
  const [avatarType, setAvatarType] = useState('photo') // 'animated' または 'photo'
  const [selectedAvatarId, setSelectedAvatarId] = useState('sato')
  const avatarRef = useRef(null)
  
  useEffect(() => {
    // 初期化時のメッセージ（受付モード時のみ）
    if (currentMode === MODES.CUSTOMER_RECEPTION) {
      if (avatarType === 'photo' && avatarService.isLoaded) {
        setAvatarMessage(avatarService.getAvatarGreeting(selectedAvatarId))
      } else {
        setAvatarMessage('こんにちは！AI受付まもりちゃんです。お手伝いできることがありましたらお声がけください。')
      }
    }
    
    // セッション状態の復元（オプション）
    const savedUser = sessionStorage.getItem('currentUser')
    if (savedUser && !currentUser) {
      try {
        const user = JSON.parse(savedUser)
        setCurrentUser(user)
        setCurrentMode(MODES.ADMIN_DASHBOARD)
      } catch (error) {
        console.warn('Failed to restore user session:', error)
        sessionStorage.removeItem('currentUser')
      }
    }
    
    // 受付状態の復元確認は削除（useState初期化時に実行済み）
  }, [currentMode, currentUser, avatarType, selectedAvatarId])
  
  // アバターサービス初期化
  useEffect(() => {
    const initializeAvatarService = async () => {
      try {
        if (!avatarService.isLoaded) {
          await avatarService.loadAvatarData()
        }
      } catch (error) {
        console.error('Failed to initialize avatar service:', error)
      }
    }
    
    initializeAvatarService()
  }, [])

  // URL-based routing
  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname
      const params = new URLSearchParams(window.location.search)
      
      // URLパスに基づいてレイアウトを決定
      if (path === '/reception-layout' || path.startsWith('/reception-layout/')) {
        setCurrentMode(MODES.CUSTOMER_RECEPTION)
      }
      
      // URLパラメータからモードを取得
      const modeParam = params.get('mode')
      if (modeParam === 'reception') {
        setCurrentMode(MODES.CUSTOMER_RECEPTION)
      } else if (modeParam === 'room-booking') {
        setCurrentMode(MODES.ROOM_BOOKING)
      }
      
      // URLパラメータからアバター設定を取得
      const avatarTypeParam = params.get('avatarType')
      const avatarIdParam = params.get('avatarId')
      
      if (avatarTypeParam && ['photo', 'animated'].includes(avatarTypeParam)) {
        setAvatarType(avatarTypeParam)
      }
      
      if (avatarIdParam && ['sato', 'tanaka', 'suzuki'].includes(avatarIdParam)) {
        setSelectedAvatarId(avatarIdParam)
      }
    }
    
    // 初期ロード時とpopstate時にルート処理
    handleRouteChange()
    window.addEventListener('popstate', handleRouteChange)
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange)
    }
  }, [])
  
  // Webカメラからの画像キャプチャ
  const captureImage = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot()
    return imageSrc
  }, [webcamRef])
  
  // 顔認証開始（従来機能）
  const startFaceAuth = async () => {
    try {
      setIsProcessing(true)
      setAvatarStatus('processing')
      setAvatarMessage('顔認証を実行しています...')
      setError('')
      
      const imageSrc = captureImage()
      if (!imageSrc) {
        throw new Error('画像のキャプチャに失敗しました')
      }
      
      // Base64データをBlobに変換
      const response = await fetch(imageSrc)
      const blob = await response.blob()
      
      // サーバーに送信
      const formData = new FormData()
      formData.append('image', blob, 'capture.jpg')
      
      const result = await fetch('http://localhost:5002/api/face-auth', {
        method: 'POST',
        body: formData
      })
      
      const data = await result.json()
      
      if (data.success) {
        setFaceResult(data.result)
        setAvatarMessage(`認証結果: ${data.result}`)
        setAvatarStatus('ready')
      } else {
        throw new Error(data.error || '顔認証に失敗しました')
      }
      
    } catch (err) {
      console.error('Face auth error:', err)
      setError(err.message)
      setAvatarMessage('顔認証でエラーが発生しました。もう一度お試しください。')
      setAvatarStatus('error')
    } finally {
      setIsProcessing(false)
    }
  }
  
  // 顔パス認証（新機能）
  const startFacePassAuth = async () => {
    try {
      setIsProcessing(true)
      setAvatarStatus('processing')
      setAvatarMessage('顔パス認証を確認しています...')
      setError('')
      
      const imageSrc = captureImage()
      if (!imageSrc) {
        throw new Error('画像のキャプチャに失敗しました')
      }
      
      const response = await fetch(imageSrc)
      const blob = await response.blob()
      
      const formData = new FormData()
      formData.append('image', blob, 'capture.jpg')
      
      const result = await fetch('http://localhost:5002/api/face-pass/verify', {
        method: 'POST',
        body: formData
      })
      
      const data = await result.json()
      
      if (data.success && data.matched) {
        setCurrentVisitor(data.visitor)
        setAvatarMessage(`いらっしゃいませ、${data.visitor.name}様！${data.visitor.meetingTitle}の件でお越しいただきありがとうございます。`)
        setAvatarStatus('ready')
        
        // 自動チェックイン処理
        await performAutoCheckIn(data.reservationId, data.visitor.name)
        
      } else {
        setAvatarMessage(data.message || '顔パス認証できませんでした。通常受付をご利用ください。')
        setAvatarStatus('ready')
      }
      
    } catch (err) {
      console.error('Face pass auth error:', err)
      setError(err.message)
      setAvatarMessage('顔パス認証でエラーが発生しました。通常受付をご利用ください。')
      setAvatarStatus('error')
    } finally {
      setIsProcessing(false)
    }
  }
  
  // 自動チェックイン処理
  const performAutoCheckIn = async (reservationId, visitorName) => {
    try {
      const response = await fetch('http://localhost:5002/api/checkin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reservationId,
          visitorName,
          faceAuthUsed: true
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setAvatarMessage(`${visitorName}様、チェックイン完了です！${data.reservation.roomName}（${data.reservation.location}）にてお待ちください。担当者に通知いたしました。`)
      }
      
    } catch (error) {
      console.error('Auto check-in error:', error)
    }
  }
  
  // 音声認識開始
  const startSpeechRecognition = async () => {
    try {
      setIsListening(true)
      setAvatarStatus('listening')
      setAvatarMessage('お話しください...')
      setError('')
      
      // Web Speech API を使用
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        throw new Error('ブラウザが音声認識をサポートしていません')
      }
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognition = new SpeechRecognition()
      
      recognition.lang = 'ja-JP'
      recognition.continuous = false
      recognition.interimResults = false
      
      recognition.onresult = async (event) => {
        const speechResult = event.results[0][0].transcript
        setSpeechText(`認識結果: ${speechResult}`)
        setAvatarMessage(`「${speechResult}」と伺いました。`)
        
        // Azure Speech APIで音声合成
        try {
          setIsSpeaking(true)
          setAvatarStatus('speaking')
          
          const response = await fetch('http://localhost:5002/api/text-to-speech', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              text: generateResponse(speechResult)
            })
          })
          
          if (response.ok) {
            const audioBlob = await response.blob()
            const audioUrl = URL.createObjectURL(audioBlob)
            const audio = new Audio(audioUrl)
            
            // アバターのリップシンクのために音声要素を設定
            setCurrentAudio(audio)
            
            // アバターにref経由で音声要素を渡す
            if (avatarRef.current) {
              avatarRef.current.setAudioElement(audio)
            }
            
            audio.onended = () => {
              setIsSpeaking(false)
              setAvatarStatus('ready')
              setAvatarMessage('他にご用件はございますか？')
              setCurrentAudio(null)
            }
            
            await audio.play()
          }
          
        } catch (ttsError) {
          console.error('TTS Error:', ttsError)
          setIsSpeaking(false)
          setAvatarStatus('ready')
        }
      }
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error)
        setError(`音声認識エラー: ${event.error}`)
        setIsListening(false)
        setAvatarStatus('error')
        setAvatarMessage('音声認識でエラーが発生しました。')
      }
      
      recognition.onend = () => {
        setIsListening(false)
        if (avatarStatus === 'listening') {
          setAvatarStatus('ready')
        }
      }
      
      recognition.start()
      
    } catch (err) {
      console.error('Speech recognition start error:', err)
      setError(err.message)
      setIsListening(false)
      setAvatarStatus('error')
      setAvatarMessage('音声認識を開始できませんでした。')
    }
  }
  
  // 20代女性受付の自然な応答生成
  const generateResponse = (userInput) => {
    const input = userInput.toLowerCase()
    
    // 写真ベースアバター使用時はアバターサービスの応答を使用
    if (avatarType === 'photo' && avatarService.isLoaded) {
      let context = 'general_inquiry'
      
      if (input.includes('会議室') || input.includes('予約')) {
        context = 'meeting_room'
      } else if (input.includes('来訪') || input.includes('面会') || input.includes('約束')) {
        context = 'visitor_registration'
      }
      
      return avatarService.generateContextualResponse(selectedAvatarId, context, userInput)
    }
    
    // 従来のアニメーションアバター用応答
    if (input.includes('会議室') || input.includes('予約')) {
      return '会議室のご予約ですね。こちらの画面から簡単にお手続きいただけます。「会議室予約」ボタンをクリックしてくださいませ。'
    } else if (input.includes('来訪') || input.includes('面会') || input.includes('約束')) {
      return 'いらっしゃいませ！本日はお忙しい中お越しいただきありがとうございます。お約束のお相手様のお名前と会社名を教えていただけますでしょうか。'
    } else if (input.includes('場所') || input.includes('どこ')) {
      return '会議室のご案内ですね。こちらのビルでは、1階に会議室A・B、そして応接室がございます。また2階には会議室Cをご用意しております。ご案内いたしましょうか。'
    } else if (input.includes('ありがと') || input.includes('感謝')) {
      return 'こちらこそありがとうございます。他にも何かご不明な点やお困りのことがございましたら、いつでもお気軽にお声かけくださいね。'
    } else if (input.includes('こんにちは') || input.includes('おはよう')) {
      return 'いらっしゃいませ！ALSOK受付の佐藤です。本日はありがとうございます。どのようなご用件でしょうか。'
    } else {
      return 'かしこまりました。すぐに確認して担当者におつなぎいたしますので、少々お待ちくださいませ。'
    }
  }
  
  // 手動チェックイン
  const handleManualCheckIn = async () => {
    const visitorName = prompt('お名前を教えてください:')
    const visitorCompany = prompt('会社名を教えてください（任意）:')
    const visitPurpose = prompt('ご用件を教えてください（任意）:')
    
    if (!visitorName) return
    
    try {
      setIsProcessing(true)
      setAvatarStatus('processing')
      setAvatarMessage('チェックイン処理中です...')
      
      const response = await fetch('http://localhost:5002/api/checkin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          visitorName,
          visitorCompany,
          visitPurpose,
          faceAuthUsed: false
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setAvatarMessage(`${visitorName}様、ありがとうございます。担当者に通知いたしました。しばらくお待ちください。`)
        setAvatarStatus('ready')
      } else {
        throw new Error(data.error || 'チェックインに失敗しました')
      }
      
    } catch (error) {
      console.error('Manual check-in error:', error)
      setError(error.message)
      setAvatarStatus('error')
      setAvatarMessage('チェックイン処理でエラーが発生しました。')
    } finally {
      setIsProcessing(false)
    }
  }
  
  const handleReservationComplete = (reservationData) => {
    setAvatarMessage('会議室の予約が完了しました！ご利用ありがとうございます。')
    
    // ユーザー役割に応じて適切なモードに遷移
    if (currentUser?.role === 'reception-only') {
      setCurrentMode(MODES.CUSTOMER_RECEPTION)
    } else {
      // 管理者・スタッフは管理ダッシュボードに戻る
      setCurrentMode(MODES.ADMIN_DASHBOARD)
    }
  }

  // ログイン処理
  const handleLogin = (user) => {
    setCurrentUser(user)
    sessionStorage.setItem('currentUser', JSON.stringify(user))
    
    // ユーザー役割に基づくデフォルトモードに移動
    const defaultMode = getDefaultModeForRole(user.role)
    setCurrentMode(defaultMode)
  }

  // ログアウト処理
  const handleLogout = () => {
    setCurrentUser(null)
    setCurrentMode(MODES.LOGIN)
    sessionStorage.removeItem('currentUser')
    // 注意: 受付状態はログアウト時にクリアしない（システム全体の状態のため）
    
    // 状態をリセット
    setAvatarMessage('')
    setFaceResult('')
    setSpeechText('')
    setCurrentVisitor(null)
    setError('')
  }

  // 受付状態変更の処理
  const handleReceptionStatusChange = useCallback((newStatus) => {
    // ゲストアカウント（reception-only）の場合は状態変更を拒否
    if (currentUser?.role === 'reception-only') {
      console.warn('⛔ App: ゲストアカウントは受付状態を変更できません')
      return false
    }
    
    console.log('📋 App: 受付状態変更開始 - 旧:', receptionStatus.status, '→ 新:', newStatus.status)
    console.log('📋 App: 新しい状態データ:', newStatus)
    
    // ローカル状態を更新
    setReceptionStatus(newStatus)
    
    // localStorageに保存
    const saved = saveReceptionStatus(newStatus)
    if (saved) {
      console.log('✅ App: localStorageに保存成功')
    } else {
      console.error('❌ App: localStorageに保存失敗')
    }
    
    return true
  }, [receptionStatus, currentUser?.role])

  // 受付モード開始
  const handleStartReception = (mode = 'customer') => {
    console.log('🟢🟢🟢 App.complete.jsx - handleStartReception called with mode:', mode) // デバッグログ
    
    if (mode === 'admin') {
      console.log('🟢 App.complete.jsx - Setting mode to ADMIN_RECEPTION') // デバッグログ
      setCurrentMode(MODES.ADMIN_RECEPTION)
      setAvatarMessage('管理者モードでAI受付システムを開始しました。')
    } else {
      console.log('🟢 App.complete.jsx - Setting mode to CUSTOMER_RECEPTION') // デバッグログ
      setCurrentMode(MODES.CUSTOMER_RECEPTION)
      setAvatarMessage('AI受付システムを開始しました。お気軽にお声がけください。')
    }
  }


  const navigateToReceptionLayout = (params = {}) => {
    const searchParams = new URLSearchParams()
    
    if (params.avatarType || avatarType) {
      searchParams.set('avatarType', params.avatarType || avatarType)
    }
    if (params.avatarId || selectedAvatarId) {
      searchParams.set('avatarId', params.avatarId || selectedAvatarId)
    }
    
    const url = `/reception-layout${searchParams.toString() ? '?' + searchParams.toString() : ''}`
    window.history.pushState({}, '', url)
    setCurrentMode(MODES.CUSTOMER_RECEPTION)
  }

  // ログイン画面
  if (currentMode === MODES.LOGIN) {
    return <LoginScreen onLogin={handleLogin} onReceptionStatusChange={null} />
  }

  // 管理画面
  if (currentMode === MODES.ADMIN_DASHBOARD) {
    return (
      <AdminPanel 
        user={currentUser}
        onStartReception={handleStartReception}
        onLogout={handleLogout}
      />
    )
  }

  // 管理者受付画面
  if (currentMode === MODES.ADMIN_RECEPTION) {
    console.log('🔵 App.complete.jsx - Rendering AdminReceptionLayout component')
    return (
      <AdminReceptionLayout
        avatarType={avatarType}
        selectedAvatarId={selectedAvatarId}
        currentUser={currentUser}
        receptionStatus={receptionStatus}
        onModeSwitch={(mode) => {
          if (mode === 'admin') {
            setCurrentMode(MODES.ADMIN_RECEPTION)
          } else {
            setCurrentMode(MODES.CUSTOMER_RECEPTION)
          }
        }}
        onNavigateToAdmin={() => setCurrentMode(MODES.ADMIN_DASHBOARD)}
      />
    )
  }

  // 詳細管理画面
  if (currentMode === 'admin') {
    return <AdminDashboard 
      onClose={() => {
        setCurrentMode(currentUser?.role === 'reception-only' ? 'reception' : 'admin-panel')
      }}
      onReceptionStatusChange={handleReceptionStatusChange}
      receptionStatus={receptionStatus}
    />
  }


  // 新しい受付レイアウト画面
  if (currentMode === MODES.CUSTOMER_RECEPTION) {
    // デバッグログを追加
    console.log('🔍 CUSTOMER_RECEPTION デバッグ情報:')
    console.log('  - receptionStatus:', receptionStatus)
    console.log('  - currentUser:', currentUser)
    console.log('  - receptionStatus.status:', receptionStatus.status)
    console.log('  - currentUser?.role:', currentUser?.role)
    
    // 停止中またはメンテナンス中の場合、ゲストアカウントはアクセス拒否
    const isMaintenanceOrStopped = receptionStatus.status === 'stopped' || receptionStatus.status === 'maintenance'
    const isGuestAccount = currentUser?.role === 'reception-only'
    
    console.log('  - isMaintenanceOrStopped:', isMaintenanceOrStopped)
    console.log('  - isGuestAccount:', isGuestAccount)
    console.log('  - 制限条件チェック結果:', isMaintenanceOrStopped && isGuestAccount)
    
    if (isMaintenanceOrStopped && isGuestAccount) {
      const isMaintenanceMode = receptionStatus.status === 'maintenance'
      
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>
              {isMaintenanceMode ? '🔧' : '⛔️'}
            </div>
            <h2 style={{ color: isMaintenanceMode ? '#ffc107' : '#dc3545', marginBottom: '16px' }}>
              {isMaintenanceMode ? 'システムメンテナンス中' : '受付を停止しています'}
            </h2>
            <p style={{ color: '#6c757d', marginBottom: '24px' }}>
              {isMaintenanceMode 
                ? (receptionStatus.maintenanceMessage || 'システムメンテナンス中です。しばらくお待ちください。')
                : '申し訳ございません。現在、受付システムを停止しております。管理者にお問い合わせください。'
              }
            </p>
          </div>
        </div>
      )
    }
    
    // 停止中の場合は管理者以外はアクセス拒否（従来の管理者向け制御は維持）
    if (receptionStatus.status === 'stopped' && currentUser?.role !== 'administrator') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⛔️</div>
            <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>受付を停止しています</h2>
            <p style={{ color: '#6c757d', marginBottom: '24px' }}>
              申し訳ございません。現在、受付システムを停止しております。<br/>
              管理者にお問い合わせください。
            </p>
          </div>
        </div>
      )
    }
    
    return (
      <ReceptionLayout
        avatarType={avatarType}
        selectedAvatarId={selectedAvatarId}
        currentUser={currentUser}
        receptionStatus={receptionStatus}
        onNavigateToAdmin={
          currentUser && canAccessMode(currentUser.role, MODES.ADMIN_DASHBOARD) 
            ? () => switchToMode(setCurrentMode, currentUser, MODES.ADMIN_DASHBOARD)
            : null
        }
      />
    )
  }

  // 会議室予約画面
  if (currentMode === MODES.ROOM_BOOKING) {
    // デバッグログを追加
    console.log('🔍 ROOM_BOOKING デバッグ情報:')
    console.log('  - receptionStatus.status:', receptionStatus.status)
    console.log('  - currentUser?.role:', currentUser?.role)
    
    // メンテナンス中の場合、ゲストアカウントはアクセス拒否
    const isMaintenanceMode = receptionStatus.status === 'maintenance'
    const isGuestAccount = currentUser?.role === 'reception-only'
    
    console.log('  - isMaintenanceMode:', isMaintenanceMode)
    console.log('  - isGuestAccount:', isGuestAccount)
    console.log('  - 制限条件チェック結果:', isMaintenanceMode && isGuestAccount)
    
    if (isMaintenanceMode && isGuestAccount) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔧</div>
            <h2 style={{ color: '#ffc107', marginBottom: '16px' }}>システムメンテナンス中</h2>
            <p style={{ color: '#6c757d', marginBottom: '24px' }}>
              {receptionStatus.maintenanceMessage || 'システムメンテナンス中です。しばらくお待ちください。'}
              <br/>会議室予約機能もご利用いただけません。
            </p>
          </div>
        </div>
      )
    }
    
    return (
      <div className="app-container">
        <div className="mode-header">
          <button 
            onClick={() => setCurrentMode(MODES.CUSTOMER_RECEPTION)}
            className="mode-button"
          >
            ← 受付に戻る
          </button>
          <h1>🏢 会議室予約システム</h1>
          {currentUser && (
            <div className="user-indicator">
              <span>{currentUser.name}</span>
              <button onClick={handleLogout} className="mini-logout">🚪</button>
            </div>
          )}
        </div>
        
        <MeetingRoomReservation onReservationComplete={handleReservationComplete} />
      </div>
    )
  }
  
  return (
    <div className="app-container">
      <div className="app-header">
        <h1>🤖 AI受付まもりちゃん</h1>
        <div className="mode-controls">
          {currentUser && canAccessMode(currentUser.role, MODES.ADMIN_DASHBOARD) && (
            <button 
              onClick={() => switchToMode(setCurrentMode, currentUser, MODES.ADMIN_DASHBOARD)}
              className="mode-button admin"
              title="管理画面"
            >
              🛡️ 管理画面
            </button>
          )}
          {currentUser && (
            <button 
              onClick={() => navigateToReceptionLayout()}
              className="mode-button primary"
              title="フルスクリーン受付"
            >
              📺 フルスクリーン受付
            </button>
          )}
          <label className="face-auth-toggle">
            <input
              type="checkbox"
              checked={faceAuthEnabled}
              onChange={(e) => setFaceAuthEnabled(e.target.checked)}
            />
            顔認証ON/OFF
          </label>
          
          <div className="avatar-controls">
            <label className="avatar-type-selector">
              <select 
                value={avatarType} 
                onChange={(e) => setAvatarType(e.target.value)}
                className="avatar-select"
              >
                <option value="photo">📸 写真アバター</option>
                <option value="animated">🎭 アニメアバター</option>
              </select>
            </label>
            
            {avatarType === 'photo' && (
              <label className="avatar-character-selector">
                <select 
                  value={selectedAvatarId} 
                  onChange={(e) => setSelectedAvatarId(e.target.value)}
                  className="avatar-select"
                >
                  <option value="sato">佐藤 美咲 (総合受付)</option>
                  <option value="tanaka">田中 愛子 (VIP受付)</option>
                  <option value="suzuki">鈴木 ひかり (案内受付)</option>
                </select>
              </label>
            )}
          </div>
        </div>
        
        {currentUser && (
          <div className="user-info-header">
            <span className="user-name">
              {currentUser.role === 'administrator' ? '👑' : 
               currentUser.role === 'staff' ? '👤' : '🎯'} {currentUser.name}
            </span>
            <button onClick={handleLogout} className="logout-btn">🚪 ログアウト</button>
          </div>
        )}
      </div>
      
      <div className="app-content">
        <div className="left-panel">
          {avatarType === 'photo' ? (
            <PhotoRealisticAvatar 
              ref={avatarRef}
              status={avatarStatus}
              message={avatarMessage}
              isListening={isListening}
              isSpeaking={isSpeaking}
              audioElement={currentAudio}
              avatarId={selectedAvatarId}
            />
          ) : (
            <Avatar 
              ref={avatarRef}
              status={avatarStatus}
              message={avatarMessage}
              isListening={isListening}
              isSpeaking={isSpeaking}
              audioElement={currentAudio}
            />
          )}
        </div>
        
        <div className="right-panel">
          <div className="camera-container-small">
            <Webcam
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="webcam-small"
              videoConstraints={{
                facingMode: 'user'
              }}
            />
            <div className="camera-overlay-small">
              <div className="face-frame-small"></div>
            </div>
          </div>
          
          <div className="control-buttons">
            {faceAuthEnabled && (
              <>
                <button 
                  onClick={startFacePassAuth}
                  disabled={isProcessing}
                  className="control-button primary"
                >
                  👤 顔パス認証
                </button>
                
                <button 
                  onClick={startFaceAuth}
                  disabled={isProcessing}
                  className="control-button"
                >
                  📸 顔認証（分析）
                </button>
              </>
            )}
            
            <button 
              onClick={startSpeechRecognition}
              disabled={isListening || isProcessing}
              className="control-button voice"
            >
              🎤 音声で話しかける
            </button>
            
            <button 
              onClick={handleManualCheckIn}
              disabled={isProcessing}
              className="control-button secondary"
            >
              ✍️ 手動チェックイン
            </button>
            
            <button 
              onClick={() => window.open('/meeting-reservation', '_blank')}
              disabled={isProcessing}
              className="control-button success"
            >
              📅 会議室予約
            </button>
          </div>
          
          <div className="info-section">
            {error && (
              <div className="error-display">
                ⚠️ {error}
              </div>
            )}
            
            {speechText && (
              <div className="speech-result">
                {speechText}
              </div>
            )}
            
            {faceResult && (
              <div className="face-result">
                {faceResult}
              </div>
            )}
            
            {currentVisitor && (
              <div className="visitor-info">
                <h3>👤 認証済み来訪者</h3>
                <p><strong>お名前:</strong> {currentVisitor.name}</p>
                <p><strong>会議:</strong> {currentVisitor.meetingTitle}</p>
                <p><strong>会議室:</strong> {currentVisitor.roomName}</p>
                <p><strong>担当者:</strong> {currentVisitor.organizerName}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style jsx="true">{`
        .app-container {
          min-height: 100vh;
          background: ${alsokTheme.gradients.security};
          font-family: ${alsokTheme.typography.fontFamily.primary};
        }
        
        .app-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 40px;
          background: rgba(0,51,102,0.15);
          backdrop-filter: blur(15px);
          border-bottom: 2px solid rgba(0,102,204,0.3);
        }
        
        .app-header h1 {
          color: ${alsokTheme.neutral.white};
          margin: 0;
          font-size: ${alsokTheme.typography.fontSize['3xl']};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          letter-spacing: 0.5px;
        }
        
        .mode-controls {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
          align-items: center;
          max-width: 800px;
          padding: 15px;
          background: rgba(255,255,255,0.1);
          border-radius: ${alsokTheme.borderRadius.xl};
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
        }
        
        .mode-button {
          background: ${alsokTheme.neutral.white};
          border: 2px solid transparent;
          padding: 10px 16px;
          border-radius: ${alsokTheme.borderRadius.lg};
          font-size: 12px;
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          cursor: pointer;
          transition: ${alsokTheme.animation.transition};
          color: ${alsokTheme.primary.main};
          box-shadow: ${alsokTheme.shadows.sm};
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          min-height: 40px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .mode-button:hover {
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.security};
          border-color: ${alsokTheme.primary.light};
        }
        
        .mode-button.primary {
          background: ${componentThemes.button.primary.background};
          color: ${componentThemes.button.primary.text};
          border-color: ${alsokTheme.primary.main};
        }
        
        .mode-button.primary:hover {
          background: ${componentThemes.button.primary.hover};
        }
        
        .mode-button.admin {
          background: ${alsokTheme.secondary.main};
          color: ${alsokTheme.neutral.white};
          border-color: ${alsokTheme.secondary.main};
        }
        
        .mode-button.admin:hover {
          background: ${alsokTheme.secondary.dark};
        }
        
        .mode-button.secondary {
          background: ${componentThemes.button.secondary.background};
          color: ${componentThemes.button.secondary.text};
          border-color: ${alsokTheme.secondary.main};
        }
        
        .mode-button.secondary:hover {
          background: ${componentThemes.button.secondary.hover};
        }
        
        .user-info-header {
          display: flex;
          align-items: center;
          gap: 15px;
          color: white;
          font-size: 14px;
        }
        
        .user-name {
          font-weight: bold;
        }
        
        .logout-btn {
          background: rgba(255,255,255,0.2);
          color: white;
          border: 1px solid rgba(255,255,255,0.3);
          padding: 8px 15px;
          border-radius: 15px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s ease;
        }
        
        .logout-btn:hover {
          background: rgba(255,255,255,0.3);
        }
        
        .user-indicator {
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
          font-size: 14px;
        }
        
        .mini-logout {
          background: rgba(255,255,255,0.2);
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 12px;
        }
        
        .face-auth-toggle {
          color: white;
          font-weight: 500;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
          padding: 8px 12px;
          background: rgba(255,255,255,0.05);
          border-radius: ${alsokTheme.borderRadius.md};
          border: 1px solid rgba(255,255,255,0.1);
          transition: all 0.3s ease;
        }
        
        .face-auth-toggle:hover {
          background: rgba(255,255,255,0.1);
        }
        
        .avatar-controls {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          grid-column: span 2;
          padding: 10px;
          background: rgba(255,255,255,0.05);
          border-radius: ${alsokTheme.borderRadius.md};
          border: 1px solid rgba(255,255,255,0.1);
        }
        
        .avatar-type-selector,
        .avatar-character-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          font-weight: 500;
          font-size: 12px;
          width: 100%;
          justify-content: center;
        }
        
        .avatar-select {
          background: rgba(255,255,255,0.9);
          border: 1px solid ${alsokTheme.primary.light};
          border-radius: ${alsokTheme.borderRadius.sm};
          padding: 6px 10px;
          font-size: 11px;
          font-weight: 500;
          color: ${alsokTheme.primary.main};
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 140px;
          max-width: 180px;
        }
        
        .avatar-select:hover {
          background: white;
          border-color: ${alsokTheme.primary.main};
          box-shadow: ${alsokTheme.shadows.sm};
        }
        
        .avatar-select:focus {
          outline: none;
          border-color: ${alsokTheme.accent.blue};
          box-shadow: 0 0 0 3px rgba(0,102,204,0.1);
        }
        
        .mode-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px 40px;
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
        }
        
        .mode-header h1 {
          color: white;
          margin: 0;
        }
        
        .app-content {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: 20px;
          padding: 20px;
          min-height: calc(100vh - 120px);
          position: relative;
        }
        
        .left-panel {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 20px;
          min-height: calc(100vh - 160px);
        }
        
        .control-buttons {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          width: 100%;
          margin-top: 15px;
          padding: 15px;
          background: rgba(255,255,255,0.08);
          border-radius: ${alsokTheme.borderRadius.lg};
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.1);
        }
        
        .control-button {
          padding: 12px 16px;
          border: 2px solid transparent;
          border-radius: ${alsokTheme.borderRadius.md};
          font-size: 13px;
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          cursor: pointer;
          transition: ${alsokTheme.animation.transition};
          background: ${alsokTheme.neutral.white};
          color: ${alsokTheme.neutral.darkGray};
          box-shadow: ${alsokTheme.shadows.sm};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          min-height: 48px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .control-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.xl};
          border-color: ${alsokTheme.primary.light};
        }
        
        .control-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .control-button.primary {
          background: ${alsokTheme.accent.green};
          color: ${alsokTheme.neutral.white};
          border-color: ${alsokTheme.accent.green};
        }
        
        .control-button.primary:hover:not(:disabled) {
          background: #1e7e34;
        }
        
        .control-button.secondary {
          background: ${alsokTheme.accent.orange};
          color: ${alsokTheme.neutral.white};
          border-color: ${alsokTheme.accent.orange};
        }
        
        .control-button.secondary:hover:not(:disabled) {
          background: #e55a2b;
        }
        
        .control-button.voice {
          background: ${alsokTheme.primary.light};
          color: ${alsokTheme.neutral.white};
          border-color: ${alsokTheme.primary.light};
        }
        
        .control-button.voice:hover:not(:disabled) {
          background: ${alsokTheme.primary.main};
        }
        
        .right-panel {
          display: flex;
          flex-direction: column;
          gap: 20px;
          position: absolute;
          top: 20px;
          right: 20px;
          width: 320px;
          z-index: 10;
        }
        
        .camera-container {
          position: relative;
          border-radius: ${alsokTheme.borderRadius.xl};
          overflow: hidden;
          box-shadow: ${alsokTheme.shadows.security};
          border: 3px solid ${alsokTheme.primary.light};
        }
        
        .camera-container-small {
          position: relative;
          width: 320px;
          aspect-ratio: 4/3;
          border-radius: ${alsokTheme.borderRadius.lg};
          overflow: hidden;
          box-shadow: ${alsokTheme.shadows.lg};
          border: 3px solid ${alsokTheme.primary.light};
          background: ${alsokTheme.neutral.black};
        }

        /* aspect-ratioサポートがないブラウザ用の代替 */
        @supports not (aspect-ratio: 4/3) {
          .camera-container-small {
            height: 240px;
          }
        }
        
        .webcam {
          width: 100%;
          height: auto;
          display: block;
        }
        
        .webcam-small {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transform: scaleX(-1);
        }
        
        .camera-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
        
        .camera-overlay-small {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
        
        .face-frame {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 200px;
          height: 200px;
          border: 3px solid ${alsokTheme.accent.green};
          border-radius: ${alsokTheme.borderRadius.full};
          animation: pulse 2s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(0,102,204,0.3);
        }
        
        .face-frame-small {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          border: 2px solid ${alsokTheme.accent.green};
          border-radius: ${alsokTheme.borderRadius.full};
          animation: pulse 2s ease-in-out infinite;
          box-shadow: 0 0 15px rgba(0,102,204,0.3);
        }
        
        .info-section {
          display: flex;
          flex-direction: column;
          gap: 15px;
          max-height: calc(100vh - 300px);
          overflow-y: auto;
        }
        
        .visitor-info {
          background: rgba(255,255,255,0.95);
          padding: ${alsokTheme.spacing.lg};
          border-radius: ${alsokTheme.borderRadius.lg};
          backdrop-filter: blur(15px);
          border: 2px solid ${alsokTheme.primary.light};
          box-shadow: ${alsokTheme.shadows.lg};
        }
        
        .visitor-info h3 {
          margin: 0 0 ${alsokTheme.spacing.md} 0;
          color: ${alsokTheme.primary.main};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          border-bottom: 2px solid ${alsokTheme.primary.light};
          padding-bottom: ${alsokTheme.spacing.sm};
        }
        
        .visitor-info p {
          margin: ${alsokTheme.spacing.sm} 0;
          color: ${alsokTheme.neutral.mediumGray};
          font-weight: ${alsokTheme.typography.fontWeight.medium};
        }
        
        .error-display, .speech-result, .face-result {
          background: rgba(255,255,255,0.95);
          padding: 15px;
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }
        
        .error-display {
          border-left: 4px solid #f44336;
          color: #d32f2f;
        }
        
        .speech-result {
          border-left: 4px solid #2196f3;
          color: #1976d2;
        }
        
        .face-result {
          border-left: 4px solid #4caf50;
          color: #388e3c;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
        }
        
        @media (max-width: 768px) {
          .app-content {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 10px;
          }
          
          .right-panel {
            position: relative;
            top: auto;
            right: auto;
            width: 100%;
            order: -1;
          }
          
          .camera-container-small {
            width: 100%;
            aspect-ratio: 4/3;
          }
          
          @supports not (aspect-ratio: 4/3) {
            .camera-container-small {
              height: 200px;
            }
          }
          
          .app-header {
            flex-direction: column;
            gap: 15px;
          }
          
          .mode-controls {
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 8px;
            padding: 12px;
            max-width: 100%;
          }
          
          .avatar-controls {
            flex-direction: column;
            gap: 8px;
            align-items: center;
            grid-column: span 1;
            padding: 8px;
          }
          
          .avatar-select {
            min-width: 120px;
            font-size: 12px;
            padding: 6px 10px;
          }
          
          .user-info-header {
            flex-direction: column;
            gap: 10px;
            text-align: center;
          }
          
          .mode-controls .mode-button {
            font-size: 11px;
            padding: 8px 12px;
            min-height: 36px;
          }
          
          .control-buttons {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 12px;
            margin-top: 10px;
          }
          
          .control-button {
            font-size: 12px;
            padding: 10px 14px;
            min-height: 44px;
          }
        }
      `}</style>
    </div>
  )
}

export default App